import React, { useCallback } from "react";
import {
  ErrorCustomBackend,
  ErrorCustomFetch,
  ErrorCustomUseHookCode,
  fetchCustomPdpECDH,
  TempErrorFrontService,
  TypingEncryptSetting,
} from "../../../utils/fetchCustomPdp";
import { URL_UPDATE_COMMERCE } from "../routes_backend";
import { notifyErrorCustom } from "../../../components/Base/NotifyCustom";
import { notifyError } from "../../../utils/notify";
import {
  ListStatusUpdate,
  TypingApplyUpdate,
  TypingStatusUpdate,
} from "../utils/utilsAll";
import { bool } from "aws-sdk/clients/signer";
import {
  Cache_Get_StatusUpdateDatetimeoldPeticion,
  Cache_Get_StatusUpdateDatetimeoldShowmodal,
  Cache_Remove_StatusUpdateDatetimeoldPeticion,
  Cache_Remove_StatusUpdateDatetimeoldShowmodal,
  Cache_Set_StatusUpdateDatetimeoldPeticion,
} from "../cache/cacheKeys";
import { useLocation, useNavigate } from "react-router-dom";
import { rutasLink } from "../routes";

//FRAGMENT ******************** TYPING *******************************

export type TypingShowmodalDatetimeout = {
  dias: number;
  horas: number;
  minutos: number;
  segundos: number;
};

export type TypingSettingFrontConsult = {
  days_countdown_actualizar: number;
  days_countdown_corregir: number;
  showmodal_datetimeout: TypingShowmodalDatetimeout;
};

export type TypingSettingFront = {
  days_countdown_actualizar: number;
  days_countdown_corregir: number;
};

export type TypingResultDetailStatusUpdate = {
  status_update: TypingStatusUpdate | undefined;
  days_countdown: number | undefined;
};

export type TypingResultConsultStatus = {
  apply_update: TypingApplyUpdate;
  detail_update: TypingResultDetailStatusUpdate;
  setting_front: TypingSettingFrontConsult;
};

export type TypingDetailStatusUpdate = {
  setting_front: TypingSettingFront;
  showmodal_render: boolean;
  status_update: TypingStatusUpdate;
  days_countdown: number | undefined;
};

//FRAGMENT ******************** CONST *******************************
const DefaultShowmodalDatetimeout: TypingShowmodalDatetimeout = {
  dias: 0,
  horas: 0,
  minutos: 1,
  segundos: 0,
};

//FRAGMENT ******************** FUNCTIONS *******************************
const converter_milisegundos = (time_event: TypingShowmodalDatetimeout) => {
  const milisegundos =
    time_event.dias * 24 * 60 * 60 * 1000 + // días a ms
    time_event.horas * 60 * 60 * 1000 + // horas a ms
    time_event.minutos * 60 * 1000 + // minutos a ms
    time_event.segundos * 1000; // segundos a ms
  return milisegundos;
};

//FRAGMENT ******************** HOOK *******************************
const usehook_name = "useHookConsultStatus";

const useHookConsultStatus = (is_load_page: bool) => {
  const location = useLocation();
  const navigate = useNavigate();

  const PeticionConsultStatusUpdate = useCallback(
    async (
      name_service: string,
      pk_comercio: number
    ): Promise<TypingResultConsultStatus> => {
      const function_name = "PeticionConsultStatusUpdate";
      let resBackend;
      try {
        const encrypt_setting: TypingEncryptSetting = {
          request_params: true,
          request_body: false,
          response_body: true,
        };
        resBackend = await fetchCustomPdpECDH(
          URL_UPDATE_COMMERCE.GET_STATUS_UPDATE,
          "GET",
          name_service,
          undefined,
          encrypt_setting,
          { pk_comercio },
          undefined,
          60,
          undefined,
          undefined,
          process.env.REACT_APP_IV_AES_ENCRYPT_COMERCIO,
          process.env.REACT_APP_IV_AES_DECRYPT_COMERCIO
        );
        const result = resBackend?.obj?.result ?? {};
        const detail_update = result?.detail_update ?? {};

        return {
          apply_update: result.apply_update,
          detail_update: {
            status_update: detail_update?.status_update
              ? detail_update?.status_update
              : undefined,
            days_countdown: detail_update?.days_countdown
              ? detail_update?.days_countdown
              : undefined,
          },
          setting_front: {
            days_countdown_actualizar:
              result?.setting_front?.days_countdown_actualizar ?? 30,
            days_countdown_corregir:
              result?.setting_front?.days_countdown_corregir ?? 7,
            showmodal_datetimeout:
              result?.setting_front?.showmodal_datetimeout ??
              DefaultShowmodalDatetimeout,
          },
        };
      } catch (error: any) {
        if (!(error instanceof ErrorCustomFetch)) {
          throw new ErrorCustomUseHookCode(
            TempErrorFrontService.replace("%s", name_service),
            error.message,
            `${usehook_name} - ${function_name}`,
            "notifyError",
            true
          );
        }
        if (error instanceof ErrorCustomBackend && error?.res) {
          resBackend = error?.res;
          if (resBackend?.obj?.ids?.id_log) {
            error.id_any = `Id Log ${resBackend?.obj?.ids?.id_log}`;
          }
        }
        if (error?.id_any)
          notifyErrorCustom(error.message, error?.id_any, 5000, {
            toastId: "verificar",
          });
        else notifyError(error.message, 5000, { toastId: "verificar" });
        throw error;
      }
    },
    []
  );

  const eventStatusUpdate = useCallback(
    async (
      id_comercio: number
    ): Promise<TypingDetailStatusUpdate | undefined> => {
      const function_name = "eventStatusUpdate";
      const name_service = "Verificación de actualización";
      const datetime_current = new Date();
      let do_consult = false;

      const cache_datetime_old_peticion =
        Cache_Get_StatusUpdateDatetimeoldPeticion();

      if (cache_datetime_old_peticion === undefined) do_consult = true;
      else {
        const diferencia = Math.abs(
          // diferencia en milisegundos
          datetime_current.getTime() - cache_datetime_old_peticion.getTime()
        );
        if (diferencia > converter_milisegundos(DefaultShowmodalDatetimeout))
          do_consult = true;
        else if (is_load_page === true) do_consult = true;
      }

      if (!do_consult) return;

      Cache_Set_StatusUpdateDatetimeoldPeticion(datetime_current);

      try {
        const result: TypingResultConsultStatus =
          await PeticionConsultStatusUpdate(name_service, id_comercio);

        if (result.apply_update !== "APPLY") {
          Cache_Remove_StatusUpdateDatetimeoldShowmodal();
          return;
        }

        if (
          !ListStatusUpdate.includes(result?.detail_update?.status_update ?? "")
        ) {
          Cache_Remove_StatusUpdateDatetimeoldShowmodal();
          return;
        }

        if (
          result.detail_update.status_update !== "ACTUALIZAR" &&
          result.detail_update.status_update !== "CORREGIR"
        ) {
          Cache_Remove_StatusUpdateDatetimeoldShowmodal();
          if (location.pathname === rutasLink.actualizar_corregir)
            navigate("../");
          return;
        }

        const detail_status_update: TypingDetailStatusUpdate = {
          status_update: result.detail_update.status_update,
          days_countdown: result?.detail_update?.days_countdown ?? 0,
          showmodal_render: true,
          setting_front: {
            days_countdown_actualizar:
              result.setting_front.days_countdown_actualizar,
            days_countdown_corregir:
              result.setting_front.days_countdown_corregir,
          },
        };

        if (location.pathname === rutasLink.actualizar_corregir) {
          Cache_Remove_StatusUpdateDatetimeoldShowmodal();
          detail_status_update.showmodal_render = false;
          return detail_status_update;
        }

        if (detail_status_update.days_countdown === 0) {
          Cache_Remove_StatusUpdateDatetimeoldShowmodal();
          detail_status_update.showmodal_render = true;
          return detail_status_update;
        }

        const cache_datetime_old_showmodal =
          Cache_Get_StatusUpdateDatetimeoldShowmodal();

        if (cache_datetime_old_showmodal === undefined) {
          detail_status_update.showmodal_render = true;
          return detail_status_update;
        }

        const diferencia = Math.abs(
          // diferencia en milisegundos
          datetime_current.getTime() - cache_datetime_old_showmodal.getTime()
        );
        if (
          diferencia >
          converter_milisegundos(result.setting_front.showmodal_datetimeout)
        ) {
          detail_status_update.showmodal_render = true;
          return detail_status_update;
        }

        detail_status_update.showmodal_render = false;
        return detail_status_update;
      } catch (error: any) {
        Cache_Remove_StatusUpdateDatetimeoldShowmodal();
        Cache_Remove_StatusUpdateDatetimeoldPeticion();
        if (!(error instanceof ErrorCustomFetch)) {
          throw new ErrorCustomUseHookCode(
            TempErrorFrontService.replace("%s", name_service),
            error.message,
            `${usehook_name} - ${function_name}`,
            "notifyError",
            true
          );
        }
      }
    },
    [PeticionConsultStatusUpdate, is_load_page, location.pathname, navigate]
  );

  return [eventStatusUpdate] as const;
};

export default useHookConsultStatus;
